import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useRef, useState } from "react";
import { Helmet } from "react-helmet";

const seasons = [
    { name: "Bed Room", images: ["bedroom1.png", "bedroom2.png", "bedroom3.png", "bedroom4.png"] },
    { name: "Bath Room", images: ["bathroom1.png", "bathroom2.png", "bathroom3.png", "bathroom4.png"] },
    { name: "Dining Room", images: ["diningroom1.png", "diningroom2.png", "diningroom3.png", "diningroom4.png"] },
    { name: "Kitchen", images: ["kitchen1.png", "kitchen2.png", "kitchen3.png", "kitchen4.png"] },
    { name: "Living Room", images: ["livingroom1.png", "livingroom2.png", "livingroom3.png", "livingroom4.png"] },
    { name: "Study Room", images: ["studyroom1.png", "studyroom2.png", "studyroom3.png", "studyroom4.png"] },
];

const getRandomUniqueOptions = (correctOption: { name: any; images?: string[]; }) => {
    const options = [correctOption];
    const availableOptions = seasons.filter(season => season.name !== correctOption.name);
    while (options.length < 4) {
        const randomIndex = Math.floor(Math.random() * availableOptions.length);
        options.push(availableOptions[randomIndex]);
        availableOptions.splice(randomIndex, 1);
    }
    return options.sort(() => Math.random() - 0.5);
};

const GuessHomeRooms = () => {
    const getNewQuestion = () => {

        document.title = "Guess the Rooms in the House – Nursery Worksheet | Mama’s Guidance"; // Set page title
        const newQuestion = seasons[Math.floor(Math.random() * seasons.length)];
        const randomImage = newQuestion.images[Math.floor(Math.random() * newQuestion.images.length)];
        return { newQuestion, newImage: randomImage, newOptions: getRandomUniqueOptions(newQuestion) };
    };

    const [{ newQuestion: question, newImage: image, newOptions: options }, setQuizState] = useState(getNewQuestion());
    const [feedback, setFeedback] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);

    const handleAnswer = (selected: string) => {
        if (selected === question.name) {
            setShowImage(true);
            setTimeout(() => {

                setQuizState(getNewQuestion());
                setFeedback("");
                setShowImage(false);

            }, 5000)
        } else {
            setFeedback("❌ Try Again!");
        }
    };

    const nextQuestion = () => {
        setShowImage(true);
        setTimeout(() => {

            setQuizState(getNewQuestion());
            setFeedback("");
            setShowImage(false);

        }, 5000)
    };

    return (
        <>
            <Helmet>
                <meta name="description" content='Help nursery kids explore their surroundings with the "Guess Home Rooms" activity on Mamas Guidance—an interactive game that teaches children to identify different rooms in a house using clues and pictures, enhancing general awareness and vocabulary.' />
            </Helmet>
            {
                showImage === false ?
                    <>
                        <div className="contentPart additionWord matchImage match-container">
                            <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                            <IonModal
                                ref={modal}
                                trigger="open-modal"
                                initialBreakpoint={1}
                                breakpoints={[0, 0.5, 0.75, 1]}
                                handleBehavior="cycle"
                                backdropDismiss={false}  // ✅ Prevents click-to-close
                                className="full-page-modal"
                            >
                                <IonContent className="ion-padding">
                                    <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                    <h1>Guess the Room in the House</h1>

                                    <p>
                                        Teach your child about different parts of their home with our engaging <strong>Guess the Room</strong> worksheet! This general awareness activity helps nursery learners <strong>identify rooms in a house</strong> based on clues and common objects found in each room.
                                    </p>

                                    <h2>🏡 What’s in the Worksheet?</h2>
                                    <p>
                                        Children will see simple questions like <strong>“Where do we cook food?”</strong> or <strong>“Which room has a bed?”</strong> accompanied by pictures of rooms such as the <p><em>kitchen, bedroom, bathroom, and living room</em></p>. They&apos;ll use their reasoning and observation skills to guess and select the correct room.
                                    </p>

                                    <h2>🎯 Learning Objectives</h2>
                                    <ul>
                                        <li>Recognize different rooms in a house</li>
                                        <li>Associate daily activities with correct rooms</li>
                                        <li>Improve critical thinking and vocabulary</li>
                                        <li>Develop awareness of personal surroundings</li>
                                    </ul>

                                    <h2>👩‍🏫 How to Use This Worksheet</h2>
                                    <ul>
                                        <li>Read each prompt aloud to the child (e.g., <strong>“Where do we take a bath?”</strong>).</li>
                                        <li>Let them look at the picture or think about their own house.</li>
                                        <li>Have them tick or circle the correct room from the given options.</li>
                                        <li>Discuss the room and what they do there for better understanding.</li>
                                    </ul>


                                    <h2>🔗 Related Topics</h2>
                                    <div>
                                        <IonItem routerLink="/nursery/general-awareness/festivals/guess-festival"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Guess Festivals</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/nursery/general-awareness/colors/pick-color"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Pick Color</IonLabel>
                                        </IonItem>
                                        <IonItem routerLink="/nursery/general-awareness/people-help-us/guess-community-helper"
                                            routerDirection="none"
                                            lines="none"
                                            detail={true}>
                                            <IonLabel>Guess Community Helper</IonLabel>
                                        </IonItem>
                                    </div>

                                    <h2>🧠 Why This Activity Matters</h2>
                                    <p>
                                        Understanding the rooms of a house helps children relate their everyday actions to spaces around them. It builds <strong>vocabulary</strong>, <strong>logical reasoning</strong>, and foundational life awareness — all crucial for early development.
                                    </p>

                                    <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                                </IonContent>
                            </IonModal>
                            <article className="visually-hidden">
                                <h1>Guess the Room in the House</h1>

                                <p>
                                    Teach your child about different parts of their home with our engaging <strong>Guess the Room</strong> worksheet! This general awareness activity helps nursery learners <strong>identify rooms in a house</strong> based on clues and common objects found in each room.
                                </p>

                                <h2>🏡 What’s in the Worksheet?</h2>
                                <p>
                                    Children will see simple questions like <strong>“Where do we cook food?”</strong> or <strong>“Which room has a bed?”</strong> accompanied by pictures of rooms such as the <p><em>kitchen, bedroom, bathroom, and living room</em></p>. They&apos;ll use their reasoning and observation skills to guess and select the correct room.
                                </p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Recognize different rooms in a house</li>
                                    <li>Associate daily activities with correct rooms</li>
                                    <li>Improve critical thinking and vocabulary</li>
                                    <li>Develop awareness of personal surroundings</li>
                                </ul>

                                <h2>👩‍🏫 How to Use This Worksheet</h2>
                                <ul>
                                    <li>Read each prompt aloud to the child (e.g., <strong>“Where do we take a bath?”</strong>).</li>
                                    <li>Let them look at the picture or think about their own house.</li>
                                    <li>Have them tick or circle the correct room from the given options.</li>
                                    <li>Discuss the room and what they do there for better understanding.</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/nursery/general-awareness/festivals/guess-festival"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Guess Festivals</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/general-awareness/colors/pick-color"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Pick Color</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/nursery/general-awareness/people-help-us/guess-community-helper"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Guess Community Helper</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>🧠 Why This Activity Matters</h2>
                                <p>
                                    Understanding the rooms of a house helps children relate their everyday actions to spaces around them. It builds vocabulary, logical reasoning, and foundational life awareness — all crucial for early development.
                                </p>
                            </article>
                            <img src={process.env.REACT_APP_API_URL + image} className="img-thumbnail season-image" style={{ height: "50%" }} />
                            <div className="mt-4" style={{ display: 'ruby' }}>
                                {options.map((option, index) => (
                                    <div key={index} style={{ margin: '10px' }}>
                                        <button className="optionButton background-blue" onClick={() => handleAnswer(option.name)}>{option.name}</button>
                                    </div>
                                ))}
                            </div>
                            <h3>{feedback}</h3>
                        </div>
                    </>
                    :
                    <>
                        <div className="contentPart showMiddle">
                            <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELDONE_LINK} />
                        </div>
                    </>
            }
        </>
    );
};

export default GuessHomeRooms;
